import React, {useState, useEffect} from 'react';

import {Box, Input, Stack, Text, Loader, ActionIcon} from '@mantine/core';
import {IconSearch, IconNotebook, IconX} from '@tabler/icons-react';
import {Sheet} from 'react-modal-sheet';
import {useNavigate} from 'react-router';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';

interface SearchResult {
    id: string;
    name: string;
    workspace_id: string;
}

interface MobileSearchSheetProps {
    isOpen: boolean;
    onClose: () => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    searchResults: SearchResult[];
    isLoading: boolean;
}

export function MobileSearchSheet({
    isOpen,
    onClose,
    searchQuery,
    onSearchChange,
    searchResults,
    isLoading,
}: MobileSearchSheetProps) {
    const navigate = useNavigate();
    const [internalOpen, setInternalOpen] = useState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setInternalOpen(true);
        }
    }, [isOpen]);

    const handleClose = () => {
        setInternalOpen(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const handleResultClick = (result: SearchResult) => {
        navigateToPathForReason(
            navigate,
            'navigate_to_project_from_mobile_search',
            `/workspaces/${result.workspace_id}/projects/${result.id}`
        );
        handleClose();
    };

    return (
        <Sheet
            isOpen={internalOpen}
            onClose={handleClose}
            snapPoints={[400, 0.85]}
            initialSnap={1}
            avoidKeyboard={true}
        >
            <Sheet.Container
                style={{
                    backgroundColor: 'var(--mantine-color-body)',
                    zIndex: 200,
                }}
            >
                <style>{`
                    .react-modal-sheet-drag-indicator {
                        display: none !important;
                    }
                `}</style>
                <Sheet.Header>
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: '12px 16px',
                        }}
                    >
                        <ActionIcon
                            variant="subtle"
                            size="lg"
                            onClick={handleClose}
                        >
                            <IconX size={24} />
                        </ActionIcon>
                    </Box>
                </Sheet.Header>
                <Sheet.Content
                    disableDrag={true}
                    style={{
                        backgroundColor: 'var(--mantine-color-body)',
                        paddingBottom: 0,
                        overflowY: 'auto',
                    }}
                >
                    <Box p="md" style={{ height: '100%' }}>
                        <Input
                            leftSection={<IconSearch size={20} />}
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            size="md"
                        />

                        <Stack gap={0} mt="md">
                            {isLoading && (
                                <Box p="md" style={{textAlign: 'center'}}>
                                    <Loader size="sm" />
                                </Box>
                            )}

                            {!isLoading && searchResults.length === 0 && searchQuery && (
                                <Box p="md" style={{textAlign: 'center'}}>
                                    <Text c="dimmed" size="sm">
                                        Nothing found...
                                    </Text>
                                </Box>
                            )}

                            {!isLoading &&
                                searchResults.map((result) => (
                                    <Box
                                        key={result.id}
                                        onClick={() => handleResultClick(result)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid var(--mantine-color-default-border)',
                                        }}
                                    >
                                        <IconNotebook size={20} />
                                        <Text size="sm">{result.name}</Text>
                                    </Box>
                                ))}
                        </Stack>
                    </Box>
                </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={handleClose} />
        </Sheet>
    );
}
